'use client';
import {
  ComponentTypeEnum,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import classNames from 'classnames';
import React from 'react';
import { sendProductAnalytics } from '../../helpers/sendAnalytics';
import { pushDataLayer } from '../../pushDataLayer';
import {
  getApplicationIdFromUrl,
  readPurchaseSnapshot,
} from '../PolicySummaryCard/usePolicySummary';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import {
  ComponentSecondaryCta,
  SecondaryCtaColorScheme,
  UrlTargetType,
} from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import { FeLink } from '$/components/FeLink';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

interface ColorClasses {
  block_fill: string;
  title: string;
  button: string;
}

const getColorSchemeClasses = (
  colorScheme?: SecondaryCtaColorScheme,
): ColorClasses => {
  switch (colorScheme) {
    case 'GREEN':
      return {
        block_fill: 'bg-highlights/green-light',
        title: 'text-highlights/green-dark',
        button:
          '!border-campaign/green-3 !text-campaign/green-3 hover:bg-campaign/green-3 hover:!text-white',
      };
    case 'BLUE':
      return {
        block_fill: 'bg-highlights/blue-light',
        title: 'text-highlights/blue-dark',
        button:
          '!border-campaign/blue-3 !text-campaign/blue-3 hover:bg-campaign/blue-3 hover:!text-white',
      };
    case 'YELLOW':
      return {
        block_fill: 'bg-highlights/yellow-light',
        title: 'text-highlights/yellow-dark',
        button:
          '!border-campaign/yellow-3 !text-campaign/yellow-3 hover:bg-campaign/yellow-3 hover:!text-white',
      };
    case 'RED':
      return {
        block_fill: 'bg-highlights/red-light',
        title: 'text-highlights/red-dark',
        button:
          '!border-campaign/red-3 !text-campaign/red-3 hover:bg-campaign/red-3 hover:!text-white',
      };
    case 'ORANGE':
      return {
        block_fill: 'bg-highlights/orange-light',
        title: 'text-highlights/orange-dark',
        button:
          '!border-campaign/orange-3 !text-campaign/orange-3 hover:bg-campaign/orange-3 hover:!text-white',
      };
    case 'WHITE':
    default:
      return {
        block_fill: 'bg-white',
        title: 'text-fill/primary-dark',
        button:
          'mhg-alpha-btn-outline-primary hover:bg-fill/primary-dark hover:!text-white',
      };
  }
};

const getCorrectButtonTarget = (button_target: UrlTargetType | undefined) => {
  return `_${button_target?.toLocaleLowerCase()}`;
};

export const SecondaryCTA: React.FC<ComponentSecondaryCta> = ({
  color_scheme,
  title,
  content,
  button_text,
  button_target,
  button_url,
  product,
}) => {
  const {
    pageData: { page },
  } = usePageDataContext();

  // The purchase-success confirmation page is marked by its Policy Summary
  // Card block (same marker PageContent uses, CMS-1794). On that page the
  // login/sign-up banner is a SecondaryCTA pointing at /login, and the
  // tracking spec (CMS-1873 / Airtable #2456) requires a login_signup_interact
  // event instead of the generic navigate push.
  const isPurchaseConfirmation = (page.components ?? []).some(
    (c) =>
      c?.__typename === 'ComponentPagesComponentPolicySummaryCardComponent',
  );

  const sendPushData = () => {
    // Resolved at click time (not render): client components also render on
    // the server, where the URL base below is unavailable.
    const isLoginBannerCta =
      isPurchaseConfirmation &&
      new URL(button_url ?? '', window.location.origin).pathname === '/login';

    if (isLoginBannerCta) {
      // marketing_consent ('yes'/'no') lives only in the purchase snapshot the
      // checkout persisted to localStorage — same source purchase_status reads.
      const snapshot = readPurchaseSnapshot(getApplicationIdFromUrl());
      pushDataLayer({
        event: 'login_signup_interact',
        intent: 'Login CTA',
        // No auth method is chosen yet at this point — the banner only leads
        // to the login page.
        login_method: 'prelogin',
        step_number: '4',
        step_name: 'purchase-confirmation',
        action: 'click',
        component_type: 'top-banner',
        component_title: 'Button_login/sign-up',
        page_location_override: window.location.href,
        marketing_consent: snapshot?.marketingConsent ?? '',
      });
      return;
    }

    pushDataLayer({
      event: 'navigate',
      component_type: 'card',
      component_title: `${title}_${button_text}`,
      action: 'click',
      index: '',
    });
    if (product) {
      sessionAttributionTracker.recordApplyClick();
      sendProductAnalytics({
        action: UserActionEnum.CLICK,
        componentTitle: `${title}_${button_text}`,
        componentType: ComponentTypeEnum.CARD,
        event: 'product_click_apply',
        index: '0',
        product: product,
      });
    }
  };

  const colorClasses = getColorSchemeClasses(color_scheme);

  return (
    <div>
      <div
        className={classNames(
          'flex h-full flex-col rounded-radius/card border border-borders/default md:flex-row',
          colorClasses.block_fill,
        )}
      >
        <div className="w-full px-spacing/padding/x py-spacing/padding/y bg-transparent">
          <div className="flex flex-col justify-between gap-spacing/gap/y md:flex-row md:items-center">
            <div className="flex flex-col gap-spacing/gap/x-xs">
              <h3
                className={classNames(
                  'mhg-alpha-typography-title-1',
                  colorClasses.title,
                )}
              >
                <FeLink
                  href={button_url}
                  onClick={(e) => {
                    e.stopPropagation();
                    sendPushData();
                  }}
                  target={getCorrectButtonTarget(button_target)}
                >
                  {title}
                </FeLink>
              </h3>
              <p className="mhg-alpha-typography-body-2 text-text/secondary">
                {content}
              </p>
            </div>
            <div className="flex w-full md:w-auto">
              <FeLink href={button_url} className="w-full">
                <Button
                  target={getCorrectButtonTarget(button_target)}
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    sendPushData();
                  }}
                  className={classNames(
                    'w-full md:w-auto',
                    colorClasses.button,
                  )}
                >
                  {button_text}
                </Button>
              </FeLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
